import { Box, Stack } from '@mui/material'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import LabeledTextField from '@/components/dataDisplay/LabeledTextField'
import type { ControllerDescription } from '@/features/controller/types'
import type { ControllerApp } from '@/features/controller/utils/controllerApp'
import { identifyControllerApp } from '@/features/controller/utils/controllerApp'

/** Label for the app version: which app the controller runs, or just "App" when that is unknown */
function renderControllerAppLabel(app: ControllerApp, t: TFunction): string {
  switch (app.kind) {
    case 'java':
      return t('controllers.firmware.java_app', { packageName: app.packageName })
    case 'rust':
      return t('controllers.firmware.rust_app', { packageName: app.packageName })
    case 'other':
      return app.packageName
    case 'unknown':
      return t('controllers.firmware.app')
  }
}

export type ControllerFirmwareDetailsProps = {
  controller: Pick<ControllerDescription, 'currentFirmware'>
}

/** The app and firmware versions the controller reported at its last boot, for the controller Details tab */
export function ControllerFirmwareDetails({ controller }: Readonly<ControllerFirmwareDetailsProps>) {
  const { t } = useTranslation()
  const firmware = controller.currentFirmware

  return (
    <Stack gap={1}>
      <CustomTypography color="grey.500" variant="caption">
        {t('controllers.firmware.title')}
      </CustomTypography>
      {firmware ? (
        <Stack direction="row" gap={2}>
          <Box flex={2}>
            <LabeledTextField
              label={renderControllerAppLabel(identifyControllerApp(firmware), t)}
              text={firmware.app.version}
            />
          </Box>
          <Box flex={1}>
            <LabeledTextField label={t('controllers.firmware.root_version')} text={firmware.root.version} />
          </Box>
          <Box flex={1}>
            <LabeledTextField label={t('controllers.firmware.jvm_version')} text={firmware.jvm.version} />
          </Box>
        </Stack>
      ) : (
        <CustomTypography variant="body2">{t('controllers.firmware.none_available')}</CustomTypography>
      )}
    </Stack>
  )
}
